import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import {
  getAgentsTargets,
  getOrganizationTargets,
} from "@/modules/targets/api/index.v2";
import AgentsTargets from "@/modules/targets/components/target-configurations/agents-targets";
import CreateOrganizationTarget from "@/modules/targets/components/target-configurations/creation-forms/create-organization-target";
import CreateTargetMultiplier from "@/modules/targets/components/target-configurations/creation-forms/create-target-multiplier";
import OrganizationTarget from "@/modules/targets/components/target-configurations/organization-targets";
import TargetsMultiplier from "@/modules/targets/components/target-configurations/targets-multiplier";
import {
  getCurrentQuarter,
  getQuarterInfo,
  getQuarterStartDate,
} from "@/modules/targets/utils";
import { queryClient } from "@/queryClient";
import { PathConstants } from "@/routes/constants";
import { muiTheme } from "@/styles/theme";
import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import TargetsConfigTemplate from "@/modules/targets/components/target-configurations/targets-template/targets-config-template";
import { useTargetsTemplateContext } from "@/modules/targets/context/targets-template.context";

const TargetsConfigurationsTable = ({
  viewOnly,
  quarter,
  year,
}: {
  viewOnly?: boolean;
  quarter?: 1 | 2 | 3 | 4;
  year?: number;
}) => {
  const { setSubDepartment } = useTargetsTemplateContext();

  const [openCreateAgentTarget, setOpenCreateAgentTarget] = useState(false);
  const [openCreateOrganizationTarget, setOpenCreateOrganizationTarget] =
    useState(false);
  const [organizationTargetSubDepartment, setOrganizationTargetSubDepartment] =
    useState("");
  const [openCreateTargetMultiplier, setOpenCreateTargetMultiplier] =
    useState(false);

  const currentQuarter = useMemo(() => {
    if (quarter && year) {
      return getQuarterInfo(getQuarterStartDate(year, quarter));
    }
    return getCurrentQuarter();
  }, [quarter, year]);

  const {
    selectedOrganization,
    selectedSingleOrganization,
    managedOrganizations,
  } = useOrganization();

  const navigate = useNavigate();

  const organizationFromParams = useParams();

  const organizationUId =
    organizationFromParams.uid ||
    selectedSingleOrganization?.uid ||
    selectedOrganization?.uid;

  const months = [currentQuarter.m1, currentQuarter.m2, currentQuarter.m3];

  const handleCreateAgentTarget = (department: {
    subDepartmentUId: string;
    subDepartmentName: string;
  }) => {
    setSubDepartment({
      uid: department.subDepartmentUId,
      name: department.subDepartmentName,
      hasTarget: true,
    });
    setOpenCreateAgentTarget(true);
  };

  const handleCreateOrganizationTarget = (department: {
    subDepartmentName: string;
    subDepartmentUId: string;
  }) => {
    setOrganizationTargetSubDepartment(department.subDepartmentName);
    setSubDepartment({
      uid: department.subDepartmentUId,
      name: department.subDepartmentName,
      hasTarget: true,
    });
    setOpenCreateOrganizationTarget(true);
  };

  const handleCreateTargetMultiplier = () => {
    setOpenCreateTargetMultiplier(true);
  };

  const {
    data: agentTargets,
    isLoading: agentTargetsLoading,
    isError: isErrorFetchingAgentsTargets,
  } = useQuery(
    [
      "agentTargets",
      currentQuarter.startDate,
      currentQuarter.endDate,
      organizationUId,
    ],
    () =>
      getAgentsTargets({
        from: currentQuarter.startDate,
        to: currentQuarter.endDate,
        organizationUIds: [organizationUId],
      }),
    {
      enabled:
        !!currentQuarter.startDate &&
        !!currentQuarter.endDate &&
        !!organizationUId,
    }
  );

  const {
    data: organizationTargets,
    isLoading: organizationTargetsLoading,
    isError: isErrorFetchingOrganizationTargets,
  } = useQuery(
    [
      `organizationTargets-${currentQuarter.startDate}-${currentQuarter.endDate}-${organizationUId}`,
      currentQuarter.startDate,
      currentQuarter.endDate,
      organizationUId,
    ],
    () =>
      getOrganizationTargets({
        from: currentQuarter.startDate,
        to: currentQuarter.endDate,
        organizationUIds: [organizationUId],
      }),
    {
      enabled:
        !!currentQuarter.startDate &&
        !!currentQuarter.endDate &&
        !!organizationUId,
    }
  );

  const handleCloseCreateAgentTarget = () => {
    setOpenCreateAgentTarget(false);
    setSubDepartment(null);
    queryClient.invalidateQueries(["agentTargets"]);
  };

  const handleCloseCreateOrganizationTarget = () => {
    setOpenCreateOrganizationTarget(false);
    setOrganizationTargetSubDepartment("");
    setSubDepartment(null);
    queryClient.invalidateQueries(["organizationTargets"]);
    queryClient.invalidateQueries([
      `organizationTargets-${currentQuarter.startDate}-${currentQuarter.endDate}-${organizationUId}`,
    ]);
  };

  const handleCloseCreateTargetMultiplier = () => {
    setOpenCreateTargetMultiplier(false);
  };

  useEffect(() => {
    if (managedOrganizations?.length && managedOrganizations?.length < 1) {
      navigate(PathConstants.HOME);
    }
  }, [managedOrganizations]);

  const organizationName = useMemo(() => {
    return managedOrganizations?.find(
      (org) => org?.organization?.uid === organizationUId
    )?.organization?.name;
  }, [managedOrganizations, organizationUId]);

  return (
    <>
      <TargetsConfigTemplate
        open={openCreateAgentTarget}
        onClose={handleCloseCreateAgentTarget}
      />

      <ResponsiveEditDialog
        open={openCreateOrganizationTarget}
        setOpen={setOpenCreateOrganizationTarget}
        handleCloseModal={handleCloseCreateOrganizationTarget}
        modalFooter={false}
        hideModalFooter={true}
        header={
          <Box sx={{ marginLeft: 3 }}>
            <Typography variant="h3_bold">{organizationName}</Typography>
          </Box>
        }
      >
        <CreateOrganizationTarget
          organizationSubDepartment={organizationTargetSubDepartment}
          onClose={handleCloseCreateOrganizationTarget}
        />
      </ResponsiveEditDialog>

      <ResponsiveEditDialog
        open={openCreateTargetMultiplier}
        setOpen={setOpenCreateTargetMultiplier}
        handleCloseModal={handleCloseCreateTargetMultiplier}
        modalFooter={false}
        hideModalFooter={true}
        header={
          <Box sx={{ marginLeft: 3 }}>
            <Typography variant="h3_bold">
              {organizationName?.toUpperCase()}
            </Typography>
          </Box>
        }
      >
        <CreateTargetMultiplier onClose={handleCloseCreateTargetMultiplier} />
      </ResponsiveEditDialog>

      <Box
        sx={
          !viewOnly
            ? {
                bgcolor: muiTheme.colorSchemes.light.palette.background.white,
                minWidth: "100%",
                p: 2,
                boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                borderRadius: 2,
              }
            : {}
        }
      >
        {!viewOnly ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Box
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[900],
                  ...muiTheme.typography.h2_med,
                }}
              >
                {organizationName} Target Configuration
              </Box>
              <Box
                sx={{
                  color: muiTheme.colorSchemes.light.palette.neutral[500],
                  ...muiTheme.typography.h4_med,
                }}
              >
                {currentQuarter.quarterLabel} (Starts{" "}
                {currentQuarter.startDate.toLocaleDateString()})
              </Box>
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.TARGETS_CONFIGURATIONS_AGENT_TARGETS,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <AgentsTargets
              viewOnly={viewOnly}
              months={months}
              onCreateAgentTarget={handleCreateAgentTarget}
              agentTargets={agentTargets}
              isLoadingAgentsTargets={agentTargetsLoading}
              isErrorFetchingAgentsTargets={isErrorFetchingAgentsTargets}
            />
          </PermissionGuard>

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.TARGETS_CONFIGURATIONS_ORGANIZATION_TARGETS,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <OrganizationTarget
              viewOnly={viewOnly}
              months={months}
              onCreateOrganizationTarget={handleCreateOrganizationTarget}
              organizationTargets={organizationTargets}
              isLoadingOrganizationTargets={organizationTargetsLoading}
              isErrorFetchingOrganizationTargets={
                isErrorFetchingOrganizationTargets
              }
            />
          </PermissionGuard>

          {!viewOnly ? (
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.TARGETS_CONFIGURATIONS_TARGET_MULTIPLIERS,
                  level: PermissionLevel.READ,
                },
              ]}
            >
              <TargetsMultiplier
                onCreateTargetMultiplier={handleCreateTargetMultiplier}
                organizationUId={organizationUId}
              />
            </PermissionGuard>
          ) : null}
        </Box>
      </Box>
    </>
  );
};

export default TargetsConfigurationsTable;
